/**
 * Base component with shared notification logic
 */
import React, { useCallback, useRef, useState } from 'react';
import { Close } from '@core/icons-react';
import {
  ErrorFilled,
  CheckmarkFilled,
  WarningFilled,
  InformationFilled
} from '@core/icons-react';
import { Tag, seg } from '../../../tags/Tag';
import type { NotificationKind, BaseNotificationProps } from './types';

// Icon mapping for different notification kinds
const iconMap = {
  error: ErrorFilled,
  success: CheckmarkFilled,
  warning: WarningFilled,
  info: InformationFilled,
};

export interface NotificationBaseProps extends BaseNotificationProps {
  /** Type of notification for styling */
  type: 'inline' | 'toast' | 'actionable' | 'callout';
  
  /** Additional CSS classes */
  className?: string;
  
  /** Inline styles */
  style?: React.CSSProperties;
  
  /** Custom icon override */
  icon?: React.ComponentType<any>;
}

export const NotificationBase: React.FC<NotificationBaseProps> = ({
  id,
  type,
  kind,
  title,
  subtitle,
  children,
  hideCloseButton = false,
  lowContrast = false,
  onClose,
  onCloseButtonClick,
  role,
  statusIconDescription,
  className = '',
  style,
  icon,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isExiting, setIsExiting] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  
  // Handle close with animation
  const handleClose = useCallback((event: MouseEvent | KeyboardEvent) => {
    if (!onClose || onClose(event as MouseEvent) !== false) {
      // Trigger exit animation
      setIsExiting(true);
      
      // After animation completes, hide the component
      setTimeout(() => {
        setIsHidden(true);
        if (onClose) {
          onClose(event as MouseEvent);
        }
      }, 250); // Match $ui-transition-moderate
    }
  }, [onClose]);

  // Handle close button click
  const handleCloseButtonClick = useCallback((event: React.MouseEvent) => {
    if (onCloseButtonClick) {
      onCloseButtonClick(event as any);
    }
    handleClose(event as any);
  }, [onCloseButtonClick, handleClose]);

  // Get the appropriate icon
  const IconComponent = icon || iconMap[kind as keyof typeof iconMap] || iconMap.info;

  // Build class names
  const baseClass = 'notification';
  const classNames = [
    baseClass,
    `${baseClass}--${type}`,
    `${baseClass}--${kind}`,
    lowContrast && `${baseClass}--low-contrast`,
    hideCloseButton && `${baseClass}--hide-close-button`,
    isExiting && `${baseClass}--exiting`,
    isHidden && `${baseClass}--exited`,
    className,
  ].filter(Boolean).join(' ');

  // Determine the appropriate role
  const notificationRole = role || (
    type === 'actionable' ? 'alertdialog' : 
    type === 'toast' ? 'status' : 
    'status'
  );

  return (
    <div
      ref={containerRef}
      className={classNames}
      role={notificationRole}
      aria-labelledby={title ? `${id}-title` : undefined}
      aria-describedby={subtitle ? `${id}-subtitle` : undefined}
      style={style}
    >
      {/* Main content area - icon + content wrapper */}
      <div className={`${baseClass}__main-content`}>
        {/* Icon */}
        {IconComponent && (
          <div className={`${baseClass}__icon-container`}>
            <IconComponent 
              className={`${baseClass}__icon`}
              size={14}
              aria-label={statusIconDescription || `${kind} icon`}
            />
          </div>
        )}

        {/* Content */}
        <div className={`${baseClass}__content`}>
          {title && (
            <div id={`${id}-title`} className={`${baseClass}__title`}>
              {title}
            </div>
          )}
          
          {subtitle && (
            <div id={`${id}-subtitle`} className={`${baseClass}__subtitle`}>
              {subtitle}
            </div>
          )}
          
          {/* Children with wrapper for margin control */}
          {children && (
            <div className={`${baseClass}__children`}>
              {children}
            </div>
          )}
        </div>
      </div>

      {/* Close button area - matches NotificationItem structure */}
      <div className={`${baseClass}__close-area`}>
        {!hideCloseButton && (
          <Tag
            template="action-sm"
            segments={[seg.icon(Close, { size: 16 })]}
            onClick={handleCloseButtonClick}
            title="Close notification"
          />
        )}
      </div>
    </div>
  );
};
